/**
 * Retorna a URL base das Edge Functions do Supabase se estiver configurada e válida.
 * Evita fetch para URL indefinida (ex.: VITE_SUPABASE_URL não definido na Vercel), que
 * resultaria em requisição para a origem errada e 404 NOT_FOUND.
 */
export function getSupabaseFunctionsBaseUrl(): string | null {
  const url = import.meta.env.VITE_SUPABASE_URL;
  if (typeof url !== "string" || !url.trim() || !url.startsWith("https://")) {
    return null;
  }
  return url.replace(/\/$/, "");
}

/**
 * Header exigido pelo gateway Supabase em chamadas `fetch` diretas às Edge Functions
 * (o `supabase.functions.invoke` envia automaticamente).
 */
export function getSupabaseApikeyHeader(): Record<string, string> {
  const k = (
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
    import.meta.env.VITE_SUPABASE_ANON_KEY ||
    ""
  ).trim();
  return k ? { apikey: k } : {};
}

/**
 * Headers para `fetch` direto às Edge Functions (gateway exige `apikey` + JWT do usuário).
 */
export function getSupabaseEdgeFunctionHeaders(
  accessToken: string | null | undefined,
  extra: Record<string, string> = {},
): Record<string, string> {
  const h: Record<string, string> = { ...getSupabaseApikeyHeader(), ...extra };
  if (accessToken) {
    h.Authorization = `Bearer ${accessToken}`;
  }
  return h;
}

/**
 * Obtém o access_token do usuário para chamadas a Edge Functions que exigem JWT de usuário.
 * Usa getSession() (cache local) por padrão — evita round-trip desnecessário ao Auth.
 * Só faz refreshSession() se o token estiver expirando em menos de 2 minutos.
 * Nunca retorna anon key — Edge Functions que validam com getUser(token) rejeitam anon.
 * @returns access_token ou null se não autenticado ou token inválido/expirado
 */
export async function getAccessTokenForEdge(): Promise<string | null> {
  const { supabase } = await import("@/shared/integrations/supabase/client");

  // Tenta cache local primeiro (sem round-trip ao servidor)
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return null;

  // Verifica se o token está válido por mais de 2 minutos
  const payload = parseJwtPayload(token);
  const expiresAt = payload?.exp ? payload.exp * 1000 : 0;
  const TWO_MINUTES = 2 * 60 * 1000;

  if (expiresAt > Date.now() + TWO_MINUTES) {
    // Token ainda válido — usa diretamente sem refresh
    return token;
  }

  // Token expirando em breve ou expirado — faz refresh
  const { data: refreshed, error } = await supabase.auth.refreshSession();
  if (error || !refreshed.session?.access_token) return null;

  const nextPayload = parseJwtPayload(refreshed.session.access_token);
  if (nextPayload?.exp && nextPayload.exp * 1000 < Date.now()) return null;
  return refreshed.session.access_token;
}

/**
 * Retorna true se o erro indica JWT inválido/expirado (401).
 * Usado para deslogar o usuário e forçar novo login.
 */
export function isInvalidJwtError(err: unknown): boolean {
  if (err && typeof err === "object") {
    const msg = (err as { message?: string }).message ?? "";
    const code = (err as { code?: number }).code;
    return (
      code === 401 ||
      /invalid jwt|invalid token|session.*expired|jwt expired/i.test(msg)
    );
  }
  return false;
}

/** Decodifica payload do JWT (base64url) sem verificar assinatura — só leitura de exp. */
function parseJwtPayload(token: string): { exp?: number } | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (b64.length % 4)) % 4;
    const padded = b64 + "=".repeat(pad);
    return JSON.parse(atob(padded)) as { exp?: number };
  } catch {
    return null;
  }
}

/**
 * Monta a URL completa para uma Edge Function (ex.: system-ai, knowledge-ai).
 * Retorna null se a base não for válida.
 */
export function getSupabaseFunctionUrl(functionName: string): string | null {
  const base = getSupabaseFunctionsBaseUrl();
  if (!base) return null;
  return `${base}/functions/v1/${functionName}`;
}
